import { Link } from '@inertiajs/react'
import { CalendarDays, ChartColumnIncreasing, Disc3, Megaphone, ShieldCheck, SplitSquareVertical } from 'lucide-react'
import FaqAccordion from '@/components/marketing/FaqAccordion'
import TrustpilotWidget from '@/components/marketing/TrustpilotWidget'
import LogoRail from '@/components/marketing/LogoRail'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import { useHomeWebMcp } from '@/hooks/useHomeWebMcp'
import type { SharedMarketingProps } from '@/types/marketing'

/* Four-card stat strip pinned to the bottom of the hero. Values match the
   v2 WAVBEE Marketing Toolkit hero spec. */
const HERO_STATS = [
  { value: '100+',     label: 'STREAMING PLATFORMS' },
  { value: '1–4 days', label: 'TO STORES' },
  { value: '90–100%',  label: 'ROYALTIES' },
  { value: '<12h',     label: 'SUPPORT RESPONSE' }
]

interface AudienceSegment {
  id: string
  label: string
  kicker: string
  title: string
  body: string
  bullets: string[]
  primary_cta: { label: string; href: string }
  secondary_cta: { label: string; href: string }
}

interface HomePageProps extends SharedMarketingProps {
  hero: {
    eyebrow: string
    title: string
    body: string
    bg_image?: string
    metrics: { value: string; label: string }[]
    social_proof?: string
    audiences: AudienceSegment[]
    visual_cards: {
      eyebrow: string
      title: string
      body: string
      accent: string
      stats: string[]
    }[]
    primary_cta: { label: string; href: string }
    secondary_cta: { label: string; href: string }
  }
  trust_metrics: { value: string; label: string; copy: string }[]
  path_cards: {
    audience: string
    title: string
    body: string
    bullets: string[]
    primary_cta: { label: string; href: string }
    secondary_cta: { label: string; href: string }
  }[]
  capabilities: { title: string; body: string; icon: string }[]
  process_steps: { step: string; title: string; copy: string }[]
  platform_intro: { eyebrow: string; title: string; body: string }
  platform_logos: { name: string; src: string }[]
  faq_items: { question: string; answer: string }[]
  final_cta: {
    title: string
    body: string
    actions: { label: string; href: string }[]
  }
  // Where the read-only WebMCP tools fetch their plan/pricing/country data from.
  // A URL, not the data: it renders nothing and only an agent can use it, so
  // shipping ~27 KB of it in every page's HTML was pure weight. See
  // useHomeWebMcp — it feature-detects before fetching.
  web_mcp_url: string
}

const iconMap = {
  calendar: CalendarDays,
  chart: ChartColumnIncreasing,
  disc: Disc3,
  megaphone: Megaphone,
  shield: ShieldCheck,
  split: SplitSquareVertical
} as const

export default function Home(props: HomePageProps) {
  // Advertise the read-only plan/pricing + country-eligibility tools to any AI
  // agent on the landing page (no-op in browsers without navigator.modelContext,
  // which is also where the fetch is skipped entirely).
  useHomeWebMcp(props.web_mcp_url)

  return (
    <>
      <section className="marketing-hero-section hero-v2 relative overflow-hidden" style={{ minHeight: '78vh' }}>
        {/* Full-bleed bg image (anchored right) + dark-to-clear left fade
            so the type column always has readable contrast. */}
        {props.hero.bg_image && (
          <div className="hero-bg" style={{ backgroundImage: `url(${props.hero.bg_image})` }} />
        )}
        <div className="hero-fade" aria-hidden="true" />
        <div className="hero-spot-warm" aria-hidden="true" />
        <div className="hero-spot-cool" aria-hidden="true" />
        <div className="hero-bottom-fade" aria-hidden="true" />

        <div className="hero-content container relative z-10 mx-auto px-6 pb-8 pt-10 lg:pb-10 lg:pt-12">
            {/* Body text — vertically centered in the empty middle */}
            <div className="hero-text">
              <span className="hero-eyebrow">Affordable · Fast · Supportive</span>

              {/* Keyword-bearing h1 for crawlers/screen readers; the visual
                  slogan below is intentionally NOT the h1 and must not change
                  appearance (.hero-headline is class-styled, so <p> renders
                  pixel-identically to the former <h1>). */}
              <h1 className="sr-only">Music distribution for artists and labels</h1>
              <p className="hero-headline">
                <span>Drop it.</span>
                <span className="grad-text">Own it.</span>
                <span>Get paid.</span>
              </p>

              <p className="hero-body">{props.hero.body}</p>

              <div className="hero-ctas">
                <a href={props.hero.primary_cta.href} className="marketing-button-primary">
                  {props.hero.primary_cta.label}
                </a>
                <Link href={props.hero.secondary_cta.href} className="marketing-button-secondary" prefetch>
                  {props.hero.secondary_cta.label}
                </Link>
              </div>
            </div>

            {/* Stat strip + trust line, pinned to the bottom */}
            <div className="hero-stats">
              <div className="hero-stat-grid">
                {HERO_STATS.map((s) => (
                  <div key={s.label} className="hero-stat-card">
                    <span className="hero-stat-value">{s.value}</span>
                    <span className="hero-stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
              {props.hero.social_proof && (
                <p className="hero-trust">{props.hero.social_proof}</p>
              )}
            </div>
        </div>
      </section>

      {/* Merged dark section: the 3-up "what we do" cards + the 2-up
          "who it's for" cards live in one <section> so the page reads
          as a single dark info zone instead of two consecutive slabs. */}
      <section className="marketing-section has-glow" style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
        <div className="section-glow warm" style={{ width: '38rem', height: '38rem', right: '-10rem', bottom: '-14rem' }} />
        <div className="container mx-auto px-6 space-y-12">
          <div className="grid gap-5 lg:grid-cols-3">
            {props.hero.visual_cards.map((card, index) => (
              <ScrollReveal key={card.title} direction="up" delay={index * 0.1}>
                <div className={`marketing-surface-card h-full accent-${card.accent}`}>
                  <div className="marketing-eyebrow-mono">{card.eyebrow}</div>
                  <h2 className="mt-2 text-xl font-semibold text-white">{card.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-white/68">{card.body}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {card.stats.map((stat) => (
                      <span key={stat} className="marketing-chip">{stat}</span>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {props.hero.audiences.map((item, index) => (
              <ScrollReveal key={item.id} direction={index === 0 ? 'left' : 'right'}>
                <div className="marketing-surface-card h-full">
                  <div className="marketing-eyebrow-mono">{item.kicker}</div>
                  <h2 className="mt-2 text-2xl font-semibold text-white">{item.title}</h2>
                  <p className="mt-3 text-sm leading-7 text-white/70">{item.body}</p>
                  <ul className="mt-5 space-y-3 text-sm text-white/82">
                    {item.bullets.map((bullet) => (
                      <li key={bullet} className="marketing-bullet-item">{bullet}</li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <a href={item.primary_cta.href} className="marketing-button-primary">
                      {item.primary_cta.label}
                    </a>
                    <Link href={item.secondary_cta.href} className="marketing-button-secondary" prefetch>
                      {item.secondary_cta.label}
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Merged cream proof zone — trust metrics + audience picker stack
          in one <section> so the page doesn't have two consecutive cream
          slabs with separate headers and identical chrome. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6 space-y-28">
          <div>
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Why artists choose wavbee</div>
                <h2 className="marketing-section-title marketing-section-title-dark">Affordable. Fast. Supportive.</h2>
              </div>
            </ScrollReveal>

            <div className="grid gap-5 lg:grid-cols-3">
              {props.trust_metrics.map((item, index) => (
                <ScrollReveal key={item.label} direction="up" delay={index * 0.1}>
                  <div className="marketing-light-card h-full">
                    <div className="marketing-eyebrow-mono">{item.value}</div>
                    <h3 className="mt-3 text-xl font-semibold text-[#151515]">{item.label}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#3b3b3b]">{item.copy}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <div>
            <ScrollReveal direction="up">
              <div className="marketing-section-header">
                <div className="marketing-eyebrow-mono">Choose your path</div>
                <h2 className="marketing-section-title marketing-section-title-dark">Whether you're an artist or a label, there's a plan for you.</h2>
              </div>
            </ScrollReveal>

            <div className="grid gap-6 lg:grid-cols-2">
              {props.path_cards.map((card, index) => (
                <ScrollReveal key={card.audience} direction={index === 0 ? 'left' : 'right'}>
                  <div className="marketing-light-card h-full">
                    <div className="marketing-eyebrow-mono">{card.audience}</div>
                    <h3 className="mt-3 text-3xl font-semibold text-[#151515]">{card.title}</h3>
                    <p className="mt-4 text-sm leading-7 text-[#3b3b3b]">{card.body}</p>
                    <ul className="mt-6 space-y-3 text-sm text-[#202020]">
                      {card.bullets.map((bullet) => (
                        <li key={bullet} className="marketing-bullet-item marketing-bullet-item-dark">{bullet}</li>
                      ))}
                    </ul>
                    <div className="mt-7 flex flex-wrap gap-3">
                      <Link href={card.primary_cta.href} className="marketing-button-primary" prefetch>
                        {card.primary_cta.label}
                      </Link>
                      <a href={card.secondary_cta.href} className="marketing-button-secondary-dark">
                        {card.secondary_cta.label}
                      </a>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section has-glow">
        <div className="section-glow cool" style={{ width: '40rem', height: '40rem', left: '-12rem', top: '-12rem' }} />
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">What you get</div>
              <h2 className="marketing-section-title">Distribution, analytics, splits, Content ID — one dashboard.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {props.capabilities.map((item, index) => {
              const Icon = iconMap[item.icon as keyof typeof iconMap] ?? Disc3

              return (
                <ScrollReveal key={item.title} direction="up" delay={index * 0.08}>
                  <div className="marketing-surface-card group h-full">
                    <div className="flex justify-end lg:justify-start">
                      <div className="marketing-icon-badge">
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <h3 className="mt-5 text-xl font-semibold text-white">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-white/72">{item.body}</p>
                  </div>
                </ScrollReveal>
              )
            })}
          </div>
        </div>
      </section>

      {/* Process steps on the cream slab — breaks the dark capabilities
          → process → logos trio into D-C-D so the page doesn't have
          three consecutive dark sections. */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">How it works</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Apply, upload, release, earn.</h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-5 xl:grid-cols-4">
            {props.process_steps.map((item, index) => (
              <ScrollReveal key={item.step} direction="up" delay={index * 0.1}>
                <div className="marketing-light-card h-full">
                  <div className="marketing-process-step">{item.step}</div>
                  <h3 className="mt-5 text-xl font-semibold text-[#151515]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-[#3b3b3b]">{item.copy}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">{props.platform_intro.eyebrow}</div>
              <h2 className="marketing-section-title">{props.platform_intro.title}</h2>
              <p className="marketing-lead max-w-3xl">{props.platform_intro.body}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="fade">
            <LogoRail items={props.platform_logos} />
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <div className="mt-8 flex justify-center">
              <Link href="/stores" className="marketing-button-secondary" prefetch>
                See all stores →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section id="faq" className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">FAQ</div>
              <h2 className="marketing-section-title marketing-section-title-dark">Frequently asked questions</h2>
            </div>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <FaqAccordion items={props.faq_items} />
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.15}>
            <div className="mt-8 text-center">
              <Link href="/help" className="text-sm font-medium text-[#0f0f0f]/60 hover:text-[#ff6a1a] transition-colors" prefetch>
                See all FAQs →
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Trustpilot */}
      <section className="container mx-auto px-6 py-8">
        <div className="mx-auto max-w-md text-center">
          <TrustpilotWidget />
        </div>
      </section>

      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-final-cta">
              <div>
                <div className="marketing-eyebrow-mono">Get started</div>
                <h2 className="marketing-section-title">{props.final_cta.title}</h2>
                <p className="marketing-lead max-w-2xl">{props.final_cta.body}</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                {props.final_cta.actions.map((action, index) => {
                  const className = index === 0 ? 'marketing-button-primary' : 'marketing-button-secondary'
                  return action.href.includes('/apply') ? (
                    <a key={action.href} href={action.href} className={className}>
                      {action.label}
                    </a>
                  ) : (
                    <Link key={action.href} href={action.href} className={className} prefetch>
                      {action.label}
                    </Link>
                  )
                })}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
